import { Injectable } from '@angular/core';
import { DocumentsAiBackendService } from './documents-ai-backend.service';
import { BoxAiRequestDto } from '../pojo/BoxAiRequestDto';
import { BehaviorSubject, EMPTY, Observable, scan, Subscription } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AiHistoryMessage, MessageType } from '../../../global-ai/utils';
import { BoxFileWithMetadataDTO } from "../../dto/BoxDTOWrapper";
import { AI_STRINGS } from "../../../ai/utils";

/** todo with abstract service or add docs why without charts https://app.clickup.com/t/86c39z3a9*/
@Injectable({
  providedIn: 'root',
})
export class DocumentsAiService {
  private _selectedFile = new BehaviorSubject<BoxFileWithMetadataDTO>(undefined);
  selectedFile$: Observable<BoxFileWithMetadataDTO> = this._selectedFile.asObservable();
  private messagesSubject = new BehaviorSubject<AiHistoryMessage[]>([]);
  messages$ = this.messagesSubject.asObservable();

  private currentStreamSubscription: Subscription;

  constructor(private documentsAiBackendService: DocumentsAiBackendService) {}

  setSelectedFile(file: BoxFileWithMetadataDTO): void {
    if (file?.id !== this._selectedFile.value?.id) {
      this.clearObservables();
    }
    this._selectedFile.next(file);
  }

  getSelectedFile(): BoxFileWithMetadataDTO {
    return this._selectedFile.value;
  }

  /**
   * Sends a streaming request to the backend service using the provided {@link BoxAiRequestDto}.
   * The method listens to the stream of responses and appends each chunk of data to the latest AI response.
   * If an error occurs during the stream, an error message is sent instead.
   */
  sendRequest(request: BoxAiRequestDto): void {
    // 1) add user message
    const userMessage = new AiHistoryMessage(MessageType.USER, request.request);
    this.appendMessage(userMessage);

    // 2) prepare assistant message, updated while streaming
    const assistantMessage = new AiHistoryMessage(MessageType.ASSISTANT, 'Thinking...', true);
    this.appendMessage(assistantMessage);

    // 3) sub to the response and update assistant message
    this.currentStreamSubscription = this.documentsAiBackendService.sendRequestGetStreamedResponse(request)
      .pipe(
        // iteratively combine each incoming chunk into a final response.
        scan((combinedResponse: string, chunk: string): string => combinedResponse + chunk, ''),
        catchError(() => {
          assistantMessage.text = AI_STRINGS.error;
          assistantMessage.isThinking = false;
          this.updateLastAssistantMessage(assistantMessage);
          return EMPTY;
        })
      )
      .subscribe((accumulatedResponse): void => {
        // update the response with the progressively accumulated response.
        assistantMessage.isThinking = false;
        assistantMessage.text = accumulatedResponse;
        this.updateLastAssistantMessage(assistantMessage);
      });
  }

  cancelActiveRequest(): void {
    if (this.currentStreamSubscription) {
      this.currentStreamSubscription.unsubscribe();
      this.currentStreamSubscription = undefined;
    }
  }

  clearObservables(): void {
    this.messagesSubject.next([]);
  }

  fetchConversationHistory(): void {
    this.documentsAiBackendService.fetchConversationHistory(this._selectedFile.value.id)
      .pipe(
        takeUntilDestroyed(),
        tap((messages: AiHistoryMessage[]) => {
          this.messagesSubject.next(messages);
        }),
        catchError((error) => {
          console.error('Error fetching conversation history:', error);
          return EMPTY;
        })).subscribe();
  }

  clearChat(): Observable<void> {
    this.clearObservables();
    return this.documentsAiBackendService.clearHistory(this._selectedFile.value.id);
  }

  private appendMessage(message: AiHistoryMessage): void {
    const currentMessages = [...this.messagesSubject.value];
    currentMessages.push(message);
    this.messagesSubject.next(currentMessages);
  }

  private updateLastAssistantMessage(newAssistantMsg: AiHistoryMessage): void {
    const currentMessages = [...this.messagesSubject.value];
    const lastIndex = currentMessages.length - 1;

    if (lastIndex >= 0 && currentMessages[lastIndex].role === MessageType.ASSISTANT) {
      currentMessages[lastIndex] = newAssistantMsg;
    } else {
      currentMessages.push(newAssistantMsg);
    }

    this.messagesSubject.next(currentMessages);
  }
}

